import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import AccountContext from '../contexts/AccountContext';
import isGeneralBoard from '../isBoardWithSlideOut';
import getDateRangePeriods from '../api/getDateRangePeriods';
import relativeDateRangeToDateRange from '../relativeDateRangeToDateRange';
import PeriodsContext from '../contexts/PeriodsContext';
import GqlClientContext from '../contexts/GqlClientContext';
import DatasetDefinitionsContext from '../contexts/DatasetDefinitionsContext';
import isPerformanceBoard from '../isPerformanceBoard';
import {
  LAST_30_DAYS_INCLUDING_TODAY,
  LAST_365_DAYS_INCLUDING_TODAY,
} from '../components/DateInput/constants';
import isoDateToAmerican from '../isoDateToAmerican';
import { V3SelectedAppContext } from './SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from './SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import STORE from '../store';
import AccountPickerContext from '../contexts/AccountPickerContext';
import useGetDatasetDefinition from '../hooks/useGetDatasetDefinition';
import { BonusDatasetPeriodsProvider } from './BonusAppProvider';
import { PERIOD_QUERY_PARAM } from '../components/PerformanceBoards/providers/SelectedEntityProvider';
import { AccountTodayOverrideContext } from './SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';

export const useGetCadence = (dataType: string) => {
  const { getPerformanceConfig } = useContext(DatasetDefinitionsContext);

  const getCadence = useCallback(() => {
    if (
      dataType === 'driverDays' ||
      dataType === 'truckDays' ||
      dataType === 'trailerDays'
    ) {
      return {
        cadence: 'day' as 'day',
        cadenceInterval: 1,
      };
    }

    if (
      dataType === 'driverWeeks' ||
      dataType === 'truckWeeks' ||
      dataType === 'trailerWeeks'
    ) {
      return {
        cadence: 'week' as 'week',
        cadenceInterval: 1,
      };
    }

    if (dataType === 'commission') {
      return {
        cadence: 'month' as 'month',
        cadenceInterval: 1,
      };
    }

    const perfConfig = getPerformanceConfig(dataType);
    if (!perfConfig) {
      return {
        cadence: 'week' as 'week',
        cadenceInterval: 1,
      };
    }

    return {
      cadence: perfConfig.interval,
      cadenceInterval: perfConfig.intervalLength,
      fixedPeriod: perfConfig.fixedPeriod,
    };
  }, [dataType, getPerformanceConfig]);

  return getCadence;
};

const usePeriods = (board: GeneralBoard | PerformanceBoardTypes.Board) => {
  const { weekStartsOn } = useContext(AccountContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { client } = useContext(GqlClientContext);
  const { getPerformanceConfig } = useContext(DatasetDefinitionsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [periods, setPeriods] = useState<Period[]>([]);
  const getCadence = useGetCadence(board.dataType);

  const getDateScope = useCallback(
    ({
      cadence,
      startOfWeek,
    }: {
      cadence: FleetOps.Interval;
      startOfWeek: WeekStartsOn;
    }): DateRangeInput => {
      const relativeDateRange: RelativeDateRange = (() => {
        if (isPerformanceBoard(board)) {
          return cadence === 'day'
            ? LAST_30_DAYS_INCLUDING_TODAY
            : LAST_365_DAYS_INCLUDING_TODAY;
        }
        return board.relativeDateRange;
      })();

      return relativeDateRangeToDateRange({
        relativeDateRange,
        startOfWeek,
        todayOverride,
      });
    },
    [board, todayOverride],
  );

  const getPeriods = useCallback(async (): Promise<Period[]> => {
    const { cadence, cadenceInterval, fixedPeriod } = getCadence();

    if (cadence === 'fixed') {
      if (!fixedPeriod) {
        const e = new Error();
        e.name = 'FixedPeriod not defined';
        throw e;
      }
      const ref = STORE.fixedPeriods.getFixedPeriodIntervalsRef({
        accountId: selectedAccountId,
        fixedPeriodId: fixedPeriod,
      });

      const periods: FixedPeriods.Period[] = [];
      const collection = await ref.get();
      collection.forEach((doc) => {
        periods.push(doc.data());
      });
      return periods;
    }

    if (!cadenceInterval) {
      const e = new Error();
      e.name = 'cadenceInterval not defined';
      throw e;
    }

    const performanceConfig = getPerformanceConfig(board.dataType);
    const startOfWeek =
      performanceConfig && performanceConfig.startOfWeek
        ? performanceConfig.startOfWeek
        : weekStartsOn;

    const dateScope = getDateScope({ cadence, startOfWeek });

    return getDateRangePeriods({
      dateScope,
      dataType: board.dataType,
      client,
      cadence,
      cadenceInterval,
    });
  }, [
    board.dataType,
    client,
    getCadence,
    getDateScope,
    getPerformanceConfig,
    selectedAccountId,
    weekStartsOn,
  ]);

  useEffect(() => {
    let isActive = true;
    getPeriods().then((newPeriods) => {
      if (!isActive) {
        return;
      }
      const sortedPeriods = newPeriods.sort((a, b) => {
        if (a.startDate < b.startDate) {
          return 1;
        } else if (a.startDate === b.startDate) {
          return 0;
        } else {
          return -1;
        }
      });

      setPeriods(sortedPeriods);
    });
    return () => {
      isActive = false;
    };
  }, [getPeriods]);

  return periods;
};

const UpcomingEventsBoardPeriodsProvider = ({
  children,
  board,
  popupDateRange,
}: {
  children: JSX.Element | JSX.Element[];
  board: GeneralBoard;
  popupDateRange?: DateRangeInput;
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period | undefined>();
  useEffect(() => {
    if (
      popupDateRange &&
      popupDateRange.startDate &&
      popupDateRange.endDate &&
      !selectedPeriod
    ) {
      const eventsPeriod: Period = {
        startDate: popupDateRange.startDate,
        endDate: popupDateRange.endDate,
        label: `${isoDateToAmerican(popupDateRange.startDate)} - ${isoDateToAmerican(popupDateRange.endDate)}`,
      };
      setSelectedPeriod(eventsPeriod);
      return;
    }
  }, [selectedPeriod, board.dataType, popupDateRange]);

  return (
    <PeriodsContext.Provider
      value={{ periods: window.emptyArray, selectedPeriod, setSelectedPeriod }}
    >
      {children}
    </PeriodsContext.Provider>
  );
};

export const BoardPeriodsProvider = ({
  children,
  board,
  initialDateRange,
}: {
  children: JSX.Element | JSX.Element[];
  board: GeneralBoard | PerformanceBoardTypes.Board;
  initialDateRange?: DateRangeInput;
}) => {
  const periods = usePeriods(board);
  const [selectedPeriod, setSelectedPeriod] = useState<Period | undefined>();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (periods.length === 0) {
      return;
    }

    const queriedStartDate = searchParams.get(PERIOD_QUERY_PARAM);

    setSelectedPeriod((current) => {
      // Keep the selection only while it is one of this board's periods. The
      // provider can outlive a change of board (e.g. switching entity view
      // board tabs), and a period from another dataset's cadence would leave
      // the board querying a window it has no period for.
      if (
        current &&
        periods.some(
          (period) =>
            period.startDate === current.startDate &&
            period.endDate === current.endDate,
        )
      ) {
        return current;
      }

      if (queriedStartDate) {
        const queriedPeriod = periods.find(
          (period) => period.startDate === queriedStartDate,
        );
        if (queriedPeriod) {
          return queriedPeriod;
        }
      }

      if (initialDateRange) {
        const matchingPeriod = periods.find(
          (p) =>
            p.startDate === initialDateRange.startDate &&
            p.endDate === initialDateRange.endDate,
        );
        if (matchingPeriod) {
          return matchingPeriod;
        }
      }

      return periods[0];
    });
  }, [initialDateRange, periods, searchParams]);

  return (
    <PeriodsContext.Provider
      value={{ periods, selectedPeriod, setSelectedPeriod }}
    >
      {children}
    </PeriodsContext.Provider>
  );
};

/**
 * Please refer to {@link BonusAppProvider} for period logic in the context of
 * a bonus portal
 *
 * We still need these board level periods for legacy solutions
 */
const Gate = ({
  children,
  board,
  initialDateRange,
}: {
  children: JSX.Element | JSX.Element[];
  board: GeneralBoard | DriverPerformanceBoard | PerformanceBoardTypes.Board;
  initialDateRange?: DateRangeInput;
}) => {
  const getDatasetDefinition = useGetDatasetDefinition();
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const datasetDefinition = useMemo(() => {
    return getDatasetDefinition(board.dataType);
  }, [board.dataType, getDatasetDefinition]);

  if (selectedProductApp && appTypeCheckers.isBonusApp(selectedProductApp)) {
    return <>{children}</>;
  }

  if (!!datasetDefinition && datasetDefinition.datasetType === 'bonus') {
    return (
      <BonusDatasetPeriodsProvider dataset={board.dataType}>
        {children}
      </BonusDatasetPeriodsProvider>
    );
  }

  if (
    isGeneralBoard(board) &&
    board.dataType === 'upcomingEvents' &&
    initialDateRange
  ) {
    return (
      <UpcomingEventsBoardPeriodsProvider
        board={board}
        popupDateRange={initialDateRange}
      >
        {children}
      </UpcomingEventsBoardPeriodsProvider>
    );
  } else if (isGeneralBoard(board) || isPerformanceBoard(board)) {
    return (
      <BoardPeriodsProvider board={board} initialDateRange={initialDateRange}>
        {children}
      </BoardPeriodsProvider>
    );
  } else {
    return <>{children}</>;
  }
};

export default Gate;
